import React from "react";
import { useQuery, useMutation } from "@apollo/client";
import { useSearchParams } from "react-router-dom";
import { getGraphQLURL } from "@/env.ts";
import SignUpForm from "./SignUpForm";
import { CHECK_TOKEN_QUERY, SIGNUP_MUTATION } from "./SignUpQueries";
import {
  NewUserSignupMutation,
  NewUserSignupMutationVariables,
} from "@/__generated__/graphql";

const SignUpPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");

  return (
    <div className="parentContainer">
      <SignUpFormContainer token={token} />
    </div>
  );
};

export default SignUpPage;

interface SignUpFormContainerProps {
  token: string | null;
}

const SignUpFormContainer: React.FC<SignUpFormContainerProps> = ({ token }) => {
  const [signup] = useMutation<
    NewUserSignupMutation,
    NewUserSignupMutationVariables
  >(SIGNUP_MUTATION, {
    context: { uri: getGraphQLURL() },
  });

  const { loading, error, data } = useQuery(CHECK_TOKEN_QUERY, {
    variables: { token },
    fetchPolicy: "network-only",
    context: { uri: getGraphQLURL() },
    skip: token === "" || !token || Array.isArray(token),
  });

  let errorMessage = "";

  if (!token || Array.isArray(token) || token === "") {
    errorMessage =
      "URL appears to be invalid. Please contact support@thehotspring.com for assistance.";
  } else if (error) {
    errorMessage = error.message;
  } else if (
    data &&
    data.isSignupTokenValid &&
    !data.isSignupTokenValid.success
  ) {
    errorMessage =
      data.isSignupTokenValid.message ||
      `You can only sign up with an invitation. If you have one already, it may have expired. Please contact support@thehotspring.com for assistance.`;
  }

  if (loading) {
    return (
      <div className="flex w-[100vw] h-[100vh] bg-background">
        <div className="w-[50vw] h-full flex flex-col items-center justify-center">
          Loading...
        </div>
      </div>
    );
  }

  if (token && !Array.isArray(token) && data?.isSignupTokenValid?.success) {
    const vendorOffice = data.isSignupTokenValid.user?.office;
    const companyName = vendorOffice?.company.profile.companyName || "";
    const office = vendorOffice?.name || "";
    const inviter = data.isSignupTokenValid.user?.inviter;
    const inviterOffice = inviter?.office;
    const inviterCompanyName =
      inviterOffice?.company.profile.companyName ||
      `${inviter?.firstName} ${inviter?.lastName}`;
    const isSignUpAgreementDisabled = !!vendorOffice?.isSignUpAgreementDisabled;
    const emailFromToken = data.isSignupTokenValid.emailFromToken || "";
    const isArtistSignup = !!data.isSignupTokenValid.user?.canBeAssignedTasks;

    return (
      <SignUpForm
        signup={signup}
        token={token}
        company={companyName}
        inviterCompany={inviterCompanyName}
        office={office}
        email={emailFromToken}
        isSignUpAgreementDisabled={isSignUpAgreementDisabled}
        isArtistSignup={isArtistSignup}
        error={errorMessage}
      />
    );
  }

  return (
    <SignUpForm
      signup={signup}
      token={token || ""}
      company=""
      inviterCompany=""
      office=""
      email=""
      isSignUpAgreementDisabled={false}
      isArtistSignup={false}
      error={errorMessage}
    />
  );
};
